import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
    Box,
    HStack,
    VStack,
    Text,
    Image,
    Table,
    Spinner,
    IconButton,
} from "@chakra-ui/react";
import { HiOutlineCpuChip, HiArrowRight } from "react-icons/hi2";
import useSetPageHeader from "@/components/hooks/useSetPageHeader";
import { ModelService } from "@/services/ModelService";

const ModelDetailPage = () => {
    const { t } = useTranslation("models");
    const { id } = useParams();
    const navigate = useNavigate();

    const [agent, setAgent] = useState(null);
    const [scenarios, setScenarios] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    useSetPageHeader(t("modelDetailPage.headerTitle"), "", <HiOutlineCpuChip />);

    useEffect(() => {
        setIsLoading(true);
        ModelService.getById(id).then((data) => {
            setAgent(data);
            setScenarios(data?.scenarios ?? []);
            setIsLoading(false);
        });
    }, [id]);

    if (isLoading) {
        return (
            <VStack w="full" py={12} align="center">
                <Spinner color="brand.400" size="lg" />
            </VStack>
        );
    }

    if (!agent) {
        return (
            <VStack w="full" py={12} align="center">
                <Text color="text.grey">{t("modelDetailPage.notFound")}</Text>
            </VStack>
        );
    }

    return (
        <VStack w="full" py={6} gap={6} align="flex-start">
            <Box
                w="full"
                bg="bg.card"
                border="1px solid"
                borderColor="border.primary"
                borderRadius={12}
                p={5}
            >
                <HStack gap={4} align="flex-start" flexWrap={{ base: 'wrap', md: 'nowrap' }}>
                    <Image
                        src={agent.avatarUrl ?? "https://placehold.co/80x80/22c55e/white?text=AI"}
                        boxSize={{ base: '60px', md: '80px' }}
                        borderRadius={8}
                        objectFit="cover"
                        flexShrink={0}
                    />
                    <VStack align="flex-start" gap={2}>
                        <Text fontWeight={700} fontSize="xl" color="white">
                            {agent.name}
                        </Text>
                        <Text color="text.grey" fontSize="sm" maxW={{ base: 'full', md: '520px' }} lineHeight={1.6}>
                            {agent.description}
                        </Text>
                    </VStack>
                </HStack>
            </Box>

            <VStack w="full" align="flex-start" gap={3}>
                <Text fontWeight={700} fontSize="lg" color="white">
                    {t("modelDetailPage.scenariosListTitle")}
                </Text>

                {/* Desktop table */}
                <Box
                    display={{ base: 'none', md: 'block' }}
                    w="full"
                    border="1px solid"
                    borderColor="border.primary"
                    borderRadius={12}
                    overflowX="auto"
                >
                    <Table.Root minW="640px">
                        <Table.Header>
                            <Table.Row bg="bg.card" borderBottom="1px solid" borderColor="border.primary">
                                <Table.ColumnHeader
                                    color="text.grey" fontWeight={600} fontSize="xs"
                                    textTransform="uppercase" letterSpacing="wider"
                                    py={3} px={5} border="none"
                                >
                                    {t("modelDetailPage.columnScenarioName")}
                                </Table.ColumnHeader>
                                <Table.ColumnHeader
                                    color="text.grey" fontWeight={600} fontSize="xs"
                                    textTransform="uppercase" letterSpacing="wider"
                                    py={3} px={5} border="none"
                                >
                                    {t("modelDetailPage.columnEditedAt")}
                                </Table.ColumnHeader>
                                <Table.ColumnHeader
                                    color="text.grey" fontWeight={600} fontSize="xs"
                                    textTransform="uppercase" letterSpacing="wider"
                                    py={3} px={5} border="none"
                                >
                                    {t("modelDetailPage.columnCreatedAt")}
                                </Table.ColumnHeader>
                                <Table.ColumnHeader
                                    color="text.grey" fontWeight={600} fontSize="xs"
                                    textTransform="uppercase" letterSpacing="wider"
                                    py={3} px={5} border="none" textAlign="right"
                                >
                                    {t("modelDetailPage.columnActions")}
                                </Table.ColumnHeader>
                            </Table.Row>
                        </Table.Header>
                        <Table.Body>
                            {scenarios.map((scenario) => (
                                <Table.Row
                                    key={scenario.id}
                                    bg="bg.secondary"
                                    borderBottom="1px solid"
                                    borderColor="border.primary"
                                    _hover={{ bg: "bg.highlight" }}
                                    cursor={'pointer'}
                                    onClick={() => navigate(`/models/${id}/scenarios/${scenario.id}`)}
                                    transition="background 0.15s"
                                >
                                    <Table.Cell py={4} px={5} border="none">
                                        <Text color="white" fontSize="sm" fontWeight={500}>
                                            {scenario.name}
                                        </Text>
                                    </Table.Cell>
                                    <Table.Cell py={4} px={5} border="none">
                                        <Text color="text.grey" fontSize="sm">
                                            {new Date(scenario.editedAtUtc).toLocaleDateString('pl-PL')}
                                        </Text>
                                    </Table.Cell>
                                    <Table.Cell py={4} px={5} border="none">
                                        <Text color="text.grey" fontSize="sm">
                                            {new Date(scenario.createdAtUtc).toLocaleDateString('pl-PL')}
                                        </Text>
                                    </Table.Cell>
                                    <Table.Cell py={4} px={5} border="none" textAlign="right">
                                        <IconButton
                                            variant="outlined"
                                            size="sm"
                                            aria-label={t("modelDetailPage.scenarioDetails")}
                                            onClick={() => navigate(`/models/${id}/scenarios/${scenario.id}`)}
                                        >
                                            <HiArrowRight />
                                        </IconButton>
                                    </Table.Cell>
                                </Table.Row>
                            ))}
                        </Table.Body>
                    </Table.Root>
                </Box>

                {/* Mobile card list */}
                <VStack
                    display={{ base: 'flex', md: 'none' }}
                    w="full"
                    gap={2}
                >
                    {scenarios.map((scenario) => (
                        <Box
                            key={scenario.id}
                            w="full"
                            bg="bg.card"
                            border="1px solid"
                            borderColor="border.primary"
                            borderRadius={10}
                            p={4}
                            cursor="pointer"
                            onClick={() => navigate(`/models/${id}/scenarios/${scenario.id}`)}
                            _active={{ opacity: 0.8 }}
                        >
                            <HStack justify="space-between" align="center">
                                <VStack align="flex-start" gap={1} flex={1} minW={0}>
                                    <Text color="white" fontSize="sm" fontWeight={600}>
                                        {scenario.name}
                                    </Text>
                                    <HStack gap={3}>
                                        <Text color="text.grey" fontSize="xs">
                                            Edytowano: {new Date(scenario.editedAtUtc).toLocaleDateString('pl-PL')}
                                        </Text>
                                        <Text color="text.grey" fontSize="xs">
                                            Utworzono: {new Date(scenario.createdAtUtc).toLocaleDateString('pl-PL')}
                                        </Text>
                                    </HStack>
                                </VStack>
                                <IconButton
                                    variant="outlined"
                                    size="sm"
                                    aria-label={t("modelDetailPage.scenarioDetails")}
                                    flexShrink={0}
                                    onClick={(e) => { e.stopPropagation(); navigate(`/models/${id}/scenarios/${scenario.id}`); }}
                                >
                                    <HiArrowRight />
                                </IconButton>
                            </HStack>
                        </Box>
                    ))}
                </VStack>
            </VStack>
        </VStack>
    );
};

export default ModelDetailPage;
